import { DefaultTheme, LocalDataKeys } from '@libs/types';
import * as React from 'react';
import { createContext, ReactNode, useContext, useEffect, useInsertionEffect, useMemo, useState } from 'react';
import { useLocalData } from '../hooks';
import { selectTheme, useAppSelector } from '../store';
import { COLOR_SCHEME_ATTRIBUTE, getOsColorScheme, resolveColorScheme, subscribeOsColorScheme, TSystemColorScheme, TThemePreference } from './colorScheme';
import { getSemanticColorsCss } from './semanticColors';
import { getThemeFonts } from './themeRender';

const ColorSchemeContext = createContext<TSystemColorScheme>(DefaultTheme.light);

/** Effective color scheme resolved by `CoreProvider` (forced scheme → kill-switch → user preference → OS). */
export const useColorScheme = (): TSystemColorScheme => useContext(ColorSchemeContext);

interface CoreProviderProps {
    children: ReactNode;
    /** Forces one scheme for a package that supports only it (e.g. lulox is dark-only). */
    colorScheme?: TSystemColorScheme;
}

/**
 * Owns the light/dark scheme of the page: resolves it, exposes it through {@link useColorScheme} and mirrors it
 * to `<html data-theme>`, which package CSS keys `color-scheme` (and so every `light-dark()` token) off.
 * Also renders the theme's fonts and its per-scheme semantic colors (see `getSemanticColorsCss`).
 */
const CoreProvider = ({ children, colorScheme: forcedScheme }: CoreProviderProps) => {
    const theme = useAppSelector(selectTheme);
    const fontsCss = useMemo(() => getThemeFonts(theme), [theme]);
    const semanticColorsCss = useMemo(() => getSemanticColorsCss(theme?.semanticColors), [theme]);
    const [osScheme, setOsScheme] = useState<TSystemColorScheme>(getOsColorScheme);
    const themePreference = useLocalData<TThemePreference>(LocalDataKeys.theme);

    useEffect(() => subscribeOsColorScheme(setOsScheme), []);

    const colorScheme = resolveColorScheme(themePreference, osScheme, forcedScheme);

    // Insertion effects run before every layout/passive effect in the tree (children's included), so nothing
    // that reads the attribute — or a style resolved from it — ever sees the previous scheme.
    useInsertionEffect(() => {
        document.documentElement.setAttribute(COLOR_SCHEME_ATTRIBUTE, colorScheme);
    }, [colorScheme]);

    return (
        <ColorSchemeContext.Provider value={colorScheme}>
            {theme?.themeHash && (
                <style href={`core-theme-${theme.themeHash}`} precedence="default">{`
                    body {
                        background: ${colorScheme === DefaultTheme.dark ? '#141414' : '#FFF'};
                        color: ${colorScheme === DefaultTheme.dark ? '#FFF' : '#141414'};
                    }
                    ${fontsCss}
                    ${semanticColorsCss}
                `}</style>
            )}
            {children}
        </ColorSchemeContext.Provider>
    );
};

export { CoreProvider };
